import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Konstanten
const SETTINGS_COLLECTION = 'appSettings';
const SETTINGS_DOC_ID = 'general';
const CF_SETTINGS_DOC_ID = 'cloudfunctions';

// Default-Werte für die Einstellungen
const defaultSettings = {
  // Treueprogramm-Modus: 'off' | 'stamp' (Treuekarte) | 'miles' (Treuemeilen).
  // Es ist immer nur genau eines aktiv. loyaltyCardActive wird daraus gespiegelt
  // (= mode === 'stamp'), damit die bestehende Treuekarten-Logik unverändert läuft.
  loyaltyProgramMode: 'off',
  // Treuekarten-Einstellungen (System 1)
  loyaltyCardActive: false,
  loyaltyCardRidesRequired: 5,
  loyaltyCardDiscountEur: 5,
  loyaltyCardStartDate: null,  // Wird beim Aktivieren automatisch gesetzt
  loyaltyCardEndDate: null,
  loyaltyCardNoEndDate: true,
  // Treuemeilen-Einstellungen (System 2) – sinnvolle Defaults (analog Treuekarte):
  // 50 gefahrene km ergeben einen 5-€-Block. Aktiv wird das System erst, wenn der
  // Modus auf 'miles' gesetzt und gespeichert wird.
  milesPerKm: 1,          // verdiente Meilen je gefahrenem km
  milesPerBlock: 50,      // Meilen je Rabatt-Block (Einlöse-Schwelle)
  milesBlockEur: 5,       // €-Rabatt je Block
  milesStartDate: null,   // Wird beim Aktivieren automatisch gesetzt
  milesEndDate: null,
  milesNoEndDate: true,
  // Vorbestellungen
  showPreordersToAllDrivers: false,
  autoAcceptPreBookings: false,
  // Betriebsgebiet für dynamische Vorbestellungs-Disposition
  serviceAreaEnabled: false,
  serviceAreaMode: 'disabled',
  serviceAreaPolygons: [],
  serviceAreaFallbackAddress: '',
  serviceAreaFallbackLat: null,
  serviceAreaFallbackLng: null,
  // Mobile App Store Links
  appStoreUrl: '',
  playStoreUrl: '',
  // Öffentliche Google-Bewertungsseite des Unternehmens (Kunden-Dialog nach der Fahrt)
  googleReviewUrl: '',
  // Tarif-Einstellungen
  tarifGrundpreis: 3.5,
  tarifPreisProKm: 2.5,
  tarifGrossraumzuschlag: 5.0,
  // Nachttarif-Einstellungen
  nachtTarifAktiv: false,
  nachtTarifGrundpreis: 3.5,
  nachtTarifPreisProKm: 2.5,
  nachtTarifWocheStart: '22:00',
  nachtTarifWocheEnd: '06:00',
  nachtTarifWochenendStart: '20:00',
  nachtTarifWochenendEnd: '07:00',
  // Anfahrtskosten außerhalb des Betriebsgebiets (Abholung UND Ziel outside).
  // Preis pro Routen-km ab Betriebsgebietsgrenze; max. tarifPreisProKm (UI-Validierung).
  anfahrtEnabled: false,
  anfahrtPreisProKm: 0.5,
  // Fahrpreis-Anzeige für Kunden
  showFareToCustomer: false,
  // Kunden-App: passenden Festpreis nach Adresseingabe automatisch übernehmen
  // (Ort+PLZ-Abgleich) statt Distanz-Schätzung. Siehe
  // nextcab_mobile/docs/general/festpreis_auto_match_konzept.md
  festpreisAutoMatchAktiv: false,
  // Fahrerabrechnung
  requireDriverFareInput: false,
  requireFareForMedicalTransportRides: true,
  requireFareForManualDriverRides: true,
  // Auto-Abschluss am Zielort (Fahrer-App-Geofence); nur relevant, wenn
  // requireDriverFareInput aus ist — sonst braucht der Abschluss den Preisdialog.
  autoCompleteRideOnArrival: false,
  driverFareInputMinEur: 0,
  driverFareInputMaxEur: 999,
  driverInactiveReasonRequired: true,
  // Fahrzeuglimit (wird manuell in Firestore gesetzt, hier nur Fallback)
  vehicleLimit: 10,
  // Routing-Default (Routes API) für die Fahrer-Navigation.
  // Der Fahrer kann diesen Standard in seinen App-Einstellungen überschreiben.
  routeShortest: false, // false = schnellste Route, true = kürzeste Strecke
  routeAvoidHighways: false,
  routeAvoidTolls: false,
  routeAvoidFerries: false,
  trafficOptimal: false, // false = TRAFFIC_AWARE, true = TRAFFIC_AWARE_OPTIMAL
  // Sofortbestellungen: Anzeige-Methode für Fahrer ('distance' | 'sectors')
  sofortbestellungAnzeigeMethode: 'distance',
  distanceExclusiveOfferSeconds: 45,
  sectorExclusiveOfferSeconds: 45,
  sectorAutoFollowUpEnabled: false,
  sectorFallbackPriorityMode: 'target_sector_first',
  // Anschlusstouren im Entfernungsmodus: besetzte Fahrer werden gleichberechtigt
  // mit freien Fahrern nach erwarteter Ankunft (ETA) bewertet.
  distanceAutoFollowUpEnabled: false,
  // Zielsektor-Reservierung ist immer aktiv und gilt fest 15 Minuten.
  // sectorShowDriverAddress steuert die Anzeige der genauen Fahrerstandorte.
  sectorShowDriverAddress: true
};

// Default-Werte für Cloud Function Einstellungen
const defaultCloudFunctionSettings = {
  // Not-Aus der Tropf-Warnkette (ohne UI, nur direkt in Firestore pflegbar)
  autoLogoutInactiveDriversEnabled: true,
  // Abmeldung nach 3 unbeantworteten Tropf-Warnungen (Switch in SettingsPage)
  dripLogoutAfterWarningsEnabled: false,
  // GPS-Historie: marken-/projektweit, ausschließlich 60 oder 90 Tage.
  driverTrackRetentionDays: 60,
};

// Listener-Referenz für Cleanup
let settingsListener = null;

// Async Thunks

/**
 * Lädt die globalen Einstellungen aus Firebase
 */
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        return {
          ...defaultSettings,
          ...data,
          // Modus abwärtskompatibel ableiten: fehlt loyaltyProgramMode in älteren
          // Dokumenten, ergibt sich der Modus aus dem bisherigen loyaltyCardActive.
          loyaltyProgramMode: data.loyaltyProgramMode
            || (data.loyaltyCardActive ? 'stamp' : 'off'),
          // Timestamps zu ISO-Strings konvertieren für Redux
          loyaltyCardStartDate: data.loyaltyCardStartDate
            ? data.loyaltyCardStartDate.toDate().toISOString().split('T')[0]
            : null,
          loyaltyCardEndDate: data.loyaltyCardEndDate
            ? data.loyaltyCardEndDate.toDate().toISOString().split('T')[0]
            : null,
          milesStartDate: data.milesStartDate
            ? data.milesStartDate.toDate().toISOString().split('T')[0]
            : null,
          milesEndDate: data.milesEndDate
            ? data.milesEndDate.toDate().toISOString().split('T')[0]
            : null,
          lastUpdated: data.lastUpdated
            ? data.lastUpdated.toDate().toISOString()
            : null
        };
      } else {
        // Dokument existiert nicht, erstelle es mit Default-Werten
        await setDoc(settingsRef, {
          ...defaultSettings,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
        return defaultSettings;
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert eine einzelne globale Einstellung sofort in Firebase
 */
export const saveSettingImmediately = createAsyncThunk(
  'settings/saveSettingImmediately',
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsDoc = await getDoc(settingsRef);

      const dataToSave = {
        [key]: value,
        lastUpdated: serverTimestamp()
      };

      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, dataToSave);
      } else {
        await setDoc(settingsRef, {
          ...defaultSettings,
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }

      return { [key]: value, lastUpdated: new Date().toISOString() };
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellung:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert mehrere globale Einstellungen atomar in einem einzigen Firestore-Write.
 *
 * Wichtig fuer zusammenhaengende Felder (z. B. Betriebsgebiet aktivieren +
 * Mittelpunkt-Koordinaten): ein einziger updateDoc loest die Cloud Function
 * `onServiceAreaSettingsChanged` nur EINMAL und immer mit vollstaendiger
 * Konfiguration aus – nie mit einem halb-konfigurierten Zwischenstand.
 */
export const saveSettingsImmediately = createAsyncThunk(
  'settings/saveSettingsImmediately',
  async ({ values }, { rejectWithValue }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsDoc = await getDoc(settingsRef);

      const dataToSave = {
        ...values,
        lastUpdated: serverTimestamp()
      };

      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, dataToSave);
      } else {
        await setDoc(settingsRef, {
          ...defaultSettings,
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }

      return { ...values, lastUpdated: new Date().toISOString() };
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert die globalen Einstellungen in Firebase
 */
export const saveSettings = createAsyncThunk(
  'settings/saveSettings',
  async (settings, { rejectWithValue, getState }) => {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);

      // Hole aktuelle Einstellungen aus dem State
      const currentSettings = getState().settings.global;

      // Treueprogramm-Modus: genau eines aktiv. loyaltyCardActive wird aus dem Modus
      // gespiegelt, damit die bestehende Treuekarten-Logik/CF unverändert weiterläuft.
      const programMode = settings.loyaltyProgramMode || 'off';
      const stampActive = programMode === 'stamp';
      const milesActive = programMode === 'miles';

      // Bereite die Daten für Firebase vor
      const dataToSave = {
        loyaltyProgramMode: programMode,
        loyaltyCardActive: stampActive,
        loyaltyCardRidesRequired: settings.loyaltyCardRidesRequired,
        loyaltyCardDiscountEur: settings.loyaltyCardDiscountEur,
        loyaltyCardNoEndDate: settings.loyaltyCardNoEndDate,
        // Treuemeilen (System 2)
        milesPerKm: settings.milesPerKm,
        milesPerBlock: settings.milesPerBlock,
        milesBlockEur: settings.milesBlockEur,
        milesNoEndDate: settings.milesNoEndDate,
        showPreordersToAllDrivers: settings.showPreordersToAllDrivers,
        autoAcceptPreBookings: settings.autoAcceptPreBookings,
        // Tarif-Einstellungen (nur speichern wenn vorhanden)
        ...(settings.tarifGrundpreis !== undefined && {
          tarifGrundpreis: settings.tarifGrundpreis,
          tarifPreisProKm: settings.tarifPreisProKm,
          tarifGrossraumzuschlag: settings.tarifGrossraumzuschlag,
        }),
        lastUpdated: serverTimestamp()
      };

      // Treuekarte: Startdatum automatisch setzen wenn gerade aktiviert
      // (nur wenn es vorher inaktiv war oder noch kein Startdatum existiert)
      if (stampActive && !currentSettings.loyaltyCardActive) {
        dataToSave.loyaltyCardStartDate = serverTimestamp();
        console.log('Treuekarten-System wird aktiviert, setze Startdatum auf jetzt');
      } else if (stampActive && currentSettings.loyaltyCardStartDate) {
        dataToSave.loyaltyCardStartDate = Timestamp.fromDate(
          new Date(currentSettings.loyaltyCardStartDate)
        );
      } else if (!stampActive) {
        // Deaktiviert -> Startdatum bleibt erhalten (für Historie)
        if (currentSettings.loyaltyCardStartDate) {
          dataToSave.loyaltyCardStartDate = Timestamp.fromDate(
            new Date(currentSettings.loyaltyCardStartDate)
          );
        } else {
          dataToSave.loyaltyCardStartDate = null;
        }
      }

      // Treuekarte: Enddatum nur speichern wenn nicht "unbestimmte Zeit"
      if (!settings.loyaltyCardNoEndDate && settings.loyaltyCardEndDate) {
        dataToSave.loyaltyCardEndDate = Timestamp.fromDate(
          new Date(settings.loyaltyCardEndDate)
        );
      } else {
        dataToSave.loyaltyCardEndDate = null;
      }

      // Treuemeilen: Startdatum automatisch setzen wenn gerade aktiviert (Modus-Wechsel auf 'miles')
      const wasMilesActive = currentSettings.loyaltyProgramMode === 'miles';
      if (milesActive && !wasMilesActive) {
        dataToSave.milesStartDate = serverTimestamp();
        console.log('Treuemeilen-System wird aktiviert, setze Startdatum auf jetzt');
      } else if (milesActive && currentSettings.milesStartDate) {
        dataToSave.milesStartDate = Timestamp.fromDate(
          new Date(currentSettings.milesStartDate)
        );
      } else if (!milesActive) {
        dataToSave.milesStartDate = currentSettings.milesStartDate
          ? Timestamp.fromDate(new Date(currentSettings.milesStartDate))
          : null;
      }

      // Treuemeilen: Enddatum nur speichern wenn nicht "unbestimmte Zeit"
      if (!settings.milesNoEndDate && settings.milesEndDate) {
        dataToSave.milesEndDate = Timestamp.fromDate(new Date(settings.milesEndDate));
      } else {
        dataToSave.milesEndDate = null;
      }

      // Prüfe ob Dokument existiert
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        await updateDoc(settingsRef, dataToSave);
      } else {
        await setDoc(settingsRef, {
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }

      console.log('Einstellungen erfolgreich gespeichert:', dataToSave);

      return {
        ...settings,
        loyaltyProgramMode: programMode,
        loyaltyCardActive: stampActive,
        loyaltyCardStartDate: stampActive && !currentSettings.loyaltyCardActive
          ? new Date().toISOString().split('T')[0]
          : currentSettings.loyaltyCardStartDate,
        milesStartDate: milesActive && !wasMilesActive
          ? new Date().toISOString().split('T')[0]
          : currentSettings.milesStartDate,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Lädt die Cloud Function Einstellungen aus Firebase (appSettings/cloudfunctions)
 */
export const fetchCloudFunctionSettings = createAsyncThunk(
  'settings/fetchCloudFunctionSettings',
  async (_, { rejectWithValue }) => {
    try {
      const cfRef = doc(db, SETTINGS_COLLECTION, CF_SETTINGS_DOC_ID);
      const cfDoc = await getDoc(cfRef);
      if (cfDoc.exists()) {
        return { ...defaultCloudFunctionSettings, ...cfDoc.data() };
      }
      return defaultCloudFunctionSettings;
    } catch (error) {
      console.error('Fehler beim Laden der CF-Einstellungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Speichert eine einzelne Cloud Function Einstellung sofort (appSettings/cloudfunctions)
 */
export const saveCloudFunctionSetting = createAsyncThunk(
  'settings/saveCloudFunctionSetting',
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const cfRef = doc(db, SETTINGS_COLLECTION, CF_SETTINGS_DOC_ID);
      await setDoc(cfRef, { [key]: value }, { merge: true });
      return { [key]: value };
    } catch (error) {
      console.error('Fehler beim Speichern der CF-Einstellung:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Startet einen Echtzeit-Listener für die Einstellungen
 */
export const startSettingsListener = () => (dispatch) => {
  console.log('Starte Einstellungen-Listener...');

  // Existierenden Listener entfernen
  if (settingsListener) {
    settingsListener();
    settingsListener = null;
  }

  const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);

  settingsListener = onSnapshot(
    settingsRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        dispatch(updateSettingsRealtime({
          ...defaultSettings,
          ...data,
          loyaltyCardStartDate: data.loyaltyCardStartDate
            ? data.loyaltyCardStartDate.toDate().toISOString().split('T')[0]
            : null,
          loyaltyCardEndDate: data.loyaltyCardEndDate
            ? data.loyaltyCardEndDate.toDate().toISOString().split('T')[0]
            : null,
          lastUpdated: data.lastUpdated
            ? data.lastUpdated.toDate().toISOString()
            : null
        }));
      }
    },
    (error) => {
      console.error('Fehler im Einstellungen-Listener:', error);
    }
  );

  return () => {
    if (settingsListener) {
      settingsListener();
      settingsListener = null;
    }
  };
};

/**
 * Stoppt den Echtzeit-Listener
 */
export const stopSettingsListener = () => () => {
  console.log('Stoppe Einstellungen-Listener...');
  if (settingsListener) {
    settingsListener();
    settingsListener = null;
  }
};

// Initial State
const initialState = {
  // Globale Einstellungen (Firebase appSettings/general)
  global: {
    ...defaultSettings,
    lastUpdated: null
  },
  // Cloud Function Einstellungen (Firebase appSettings/cloudfunctions)
  cloudFunctions: {
    ...defaultCloudFunctionSettings,
  },
  // Lokale Einstellungen (localStorage)
  local: {
    darkMode: false,
    soundOnInstantOrder: true,
    soundOnPreorder: true
  },
  // Status
  isLoading: false,
  isSaving: false,
  error: null,
  isInitialized: false
};

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Lokale Einstellungen aktualisieren
    updateLocalSettings: (state, action) => {
      state.local = {
        ...state.local,
        ...action.payload
      };
      // In localStorage speichern
      try {
        localStorage.setItem('localSettings', JSON.stringify(state.local));
      } catch (error) {
        console.error('Fehler beim Speichern in localStorage:', error);
      }
    },
    // Lokale Einstellungen aus localStorage laden
    loadLocalSettings: (state) => {
      try {
        const saved = localStorage.getItem('localSettings');
        if (saved) {
          state.local = {
            ...state.local,
            ...JSON.parse(saved)
          };
        }
      } catch (error) {
        console.error('Fehler beim Laden aus localStorage:', error);
      }
    },
    // Echtzeit-Update der globalen Einstellungen
    updateSettingsRealtime: (state, action) => {
      state.global = {
        ...state.global,
        ...action.payload
      };
      state.isInitialized = true;
    },
    // Fehler zurücksetzen
    clearSettingsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.global = {
          ...state.global,
          ...action.payload
        };
        state.isInitialized = true;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save Settings
      .addCase(saveSettings.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveSettings.fulfilled, (state, action) => {
        state.isSaving = false;
        state.global = {
          ...state.global,
          ...action.payload
        };
      })
      .addCase(saveSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      // Save Setting Immediately - Optimistisches Update
      .addCase(saveSettingImmediately.pending, (state, action) => {
        const { key, value } = action.meta.arg;
        state.global[key] = value;
      })
      .addCase(saveSettingImmediately.fulfilled, (state, action) => {
        state.global = {
          ...state.global,
          ...action.payload
        };
      })
      .addCase(saveSettingImmediately.rejected, (state, action) => {
        // Optimistisches Update rückgängig machen
        const { key, value } = action.meta.arg;
        state.global[key] = !value;
        state.error = action.payload;
      })
      // Save Settings Immediately (atomar, mehrere Felder)
      .addCase(saveSettingsImmediately.fulfilled, (state, action) => {
        state.global = {
          ...state.global,
          ...action.payload
        };
      })
      .addCase(saveSettingsImmediately.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Fetch Cloud Function Settings
      .addCase(fetchCloudFunctionSettings.fulfilled, (state, action) => {
        state.cloudFunctions = { ...state.cloudFunctions, ...action.payload };
      })
      // Save Cloud Function Setting - Optimistisches Update
      .addCase(saveCloudFunctionSetting.pending, (state, action) => {
        const { key, value } = action.meta.arg;
        state.cloudFunctions[key] = value;
      })
      .addCase(saveCloudFunctionSetting.fulfilled, (state, action) => {
        state.cloudFunctions = { ...state.cloudFunctions, ...action.payload };
      })
      .addCase(saveCloudFunctionSetting.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const {
  updateLocalSettings,
  loadLocalSettings,
  updateSettingsRealtime,
  clearSettingsError
} = settingsSlice.actions;

export default settingsSlice.reducer;
